const CART_EVENTS = {
  UPDATED: 'theme:cart:updated',
}

function formatMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(Number(cents) / 100)
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency || ''}`.trim()
  }
}

async function parseJsonResponse(response) {
  const text = await response.text()
  try {
    return JSON.parse(text)
  } catch {
    return { message: text || response.statusText }
  }
}

export async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  if (!response.ok) throw new Error('Unable to load cart')
  return response.json()
}

export async function addLineItems(items) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ items }),
  })
  const data = await parseJsonResponse(response)
  if (!response.ok) {
    const message =
      data?.description || data?.message || data?.errors || 'Could not add to cart'
    throw new Error(typeof message === 'string' ? message : JSON.stringify(message))
  }
  return data
}

export async function updateCart(updates) {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ updates }),
  })
  const data = await parseJsonResponse(response)
  if (!response.ok) {
    throw new Error(data?.description || data?.message || 'Could not update cart')
  }
  return data
}

export async function changeLine(line, quantity) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ line, quantity }),
  })
  const data = await parseJsonResponse(response)
  if (!response.ok) {
    throw new Error(data?.description || data?.message || 'Could not change line item')
  }
  return data
}

// Flexible variant of `changeLine` — accepts any subset of Cart Ajax change.js
// attributes (`quantity`, `selling_plan`, `properties`, `id`). Used for
// subscription toggling and selling-plan changes (Skio uses Shopify's native
// selling plans). Pass `sections` (string or array) to fetch rendered section
// HTML in the same request — much cheaper than an extra round trip.
export async function updateLine(line, attributes, sections) {
  const body = { line }
  if (attributes) Object.assign(body, attributes)
  if (sections) {
    body.sections = Array.isArray(sections) ? sections.join(',') : sections
  }
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  })
  const data = await parseJsonResponse(response)
  if (!response.ok) {
    throw new Error(data?.description || data?.message || 'Could not update line item')
  }
  return data
}

// Product-card add feedback: instead of a text message, briefly swap the card's
// Add To Cart button label to a confirmation, then restore the original markup.
const CARD_ADDED_LABEL = 'Added To Cart!'
const CARD_ADDED_DURATION_MS = 5000
const cardAddedState = new WeakMap()

function flashCardAdded(button) {
  const existing = cardAddedState.get(button)
  if (existing) {
    clearTimeout(existing.timer)
  } else {
    // Remember the original markup (label spans) so we can restore it later.
    cardAddedState.set(button, { html: button.innerHTML, timer: 0 })
  }
  button.textContent = CARD_ADDED_LABEL
  const state = cardAddedState.get(button)
  state.timer = window.setTimeout(() => {
    button.innerHTML = state.html
    cardAddedState.delete(button)
  }, CARD_ADDED_DURATION_MS)
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}

function renderDrawer(cart, root) {
  const mount = root.querySelector('[data-cart-drawer-content]')
  if (!mount) return

  if (!cart.item_count) {
    mount.innerHTML = `<p>Your cart is empty.</p><p><a href="/cart">View cart</a></p>`
    return
  }

  const currency = cart.currency || 'USD'
  const lines = cart.items
    .map(
      (item, index) => `
      <li class="cart-drawer-line">
        <span>${escapeHtml(item.product_title)} &times; ${item.quantity}</span>
        <span>${formatMoney(item.final_line_price, currency)}</span>
        <button type="button" class="cart-drawer-remove" data-cart-remove-line="${index + 1}">Remove</button>
      </li>`,
    )
    .join('')

  mount.innerHTML = `
    <p><strong>Cart</strong></p>
    <ul class="cart-drawer-list">${lines}</ul>
    <p><a href="/cart">View cart</a></p>`
}

function renderCartPage(cart, root) {
  const mount = root.querySelector('[data-cart-page-content]')
  if (!mount) return

  if (!cart.item_count) {
    mount.innerHTML = `
      <p>Your cart is empty.</p>
      <p><a href="/">Continue shopping</a></p>`
    return
  }

  const currency = cart.currency || 'USD'
  const rows = cart.items
    .map(
      (item) => `
      <tr>
        <td><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></td>
        <td>
          <input
            type="number"
            min="0"
            value="${item.quantity}"
            data-cart-line-key="${escapeHtml(item.key)}"
            aria-label="Quantity for ${escapeHtml(item.product_title)}"
          >
        </td>
        <td>${formatMoney(item.final_line_price, currency)}</td>
      </tr>`,
    )
    .join('')

  mount.innerHTML = `
    <table class="cart-page-table">
      <thead>
        <tr>
          <th>Product</th>
          <th>Quantity</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="cart-page-actions">
      <button type="button" data-cart-page-update>Update cart</button>
      <button type="button" data-cart-page-checkout>Checkout</button>
    </div>`
}

function updateCount(cart) {
  document.querySelectorAll('[data-cart-count]').forEach((node) => {
    node.textContent = String(cart.item_count)
  })
}

async function publishCart(cart) {
  updateCount(cart)
  document.querySelectorAll('[data-cart-drawer]').forEach((root) => renderDrawer(cart, root))
  document.querySelectorAll('[data-cart-page]').forEach((root) => renderCartPage(cart, root))
  document.dispatchEvent(new CustomEvent(CART_EVENTS.UPDATED, { detail: { cart } }))
}

export async function refreshCart() {
  const cart = await fetchCart()
  await publishCart(cart)
  return cart
}

function bindDrawerRemove(root) {
  root.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-remove-line]')
    if (!button) return
    const line = Number(button.getAttribute('data-cart-remove-line'), 10)
    if (!line) return
    try {
      await changeLine(line, 0)
      await refreshCart()
    } catch (error) {
      console.error(error)
    }
  })
}

function bindProductForms() {
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form')
    const root = form?.closest('[data-ajax-product-form]')
    if (!form || !root) return
    event.preventDefault()

    const variantInput = form.querySelector('[name="id"]')
    const quantityInput = form.querySelector('[name="quantity"]')
    const sellingPlanInput = form.querySelector('[name="selling_plan"]')
    const message = root.querySelector('[data-ajax-product-form-message]')

    const id = Number(variantInput?.value, 10)
    const quantity = Math.max(1, Number(quantityInput?.value || 1, 10))
    const sellingPlanRaw = sellingPlanInput?.value?.trim()
    const sellingPlanId = sellingPlanRaw ? Number(sellingPlanRaw, 10) : 0

    if (!id) {
      if (message) {
        message.textContent = 'Select a variant'
        message.classList.add('product-form-message-error')
      }
      return
    }

    if (message) {
      message.textContent = ''
      message.classList.remove('product-form-message-error', 'product-form-message-success')
    }

    const lineItem = { id, quantity }
    if (sellingPlanId) {
      lineItem.selling_plan = sellingPlanId
    }

    try {
      await addLineItems([lineItem])
      await refreshCart()
      const cardAddBtn = form.querySelector('[data-product-card-add]')
      if (cardAddBtn) {
        flashCardAdded(cardAddBtn)
      } else if (message) {
        message.textContent = 'Added to cart'
        message.classList.add('product-form-message-success')
      }
    } catch (error) {
      if (message) {
        message.textContent = error.message
        message.classList.add('product-form-message-error')
      }
    }
  })
}

function bindCartPage(root) {
  root.addEventListener('click', async (event) => {
    const updateButton = event.target.closest('[data-cart-page-update]')
    const checkoutButton = event.target.closest('[data-cart-page-checkout]')

    if (updateButton) {
      event.preventDefault()
      const updates = {}
      root.querySelectorAll('[data-cart-line-key]').forEach((input) => {
        const key = input.getAttribute('data-cart-line-key')
        if (!key) return
        updates[key] = Number(input.value, 10)
      })
      try {
        await updateCart(updates)
        await refreshCart()
      } catch (error) {
        console.error(error)
      }
      return
    }

    if (checkoutButton) {
      event.preventDefault()
      const updates = {}
      root.querySelectorAll('[data-cart-line-key]').forEach((input) => {
        const key = input.getAttribute('data-cart-line-key')
        if (!key) return
        updates[key] = Number(input.value, 10)
      })
      try {
        if (Object.keys(updates).length) {
          await updateCart(updates)
        }
      } catch (error) {
        console.error(error)
        return
      }
      window.location.href = '/checkout'
    }
  })
}

export function initThemeCart() {
  bindProductForms()
  document.querySelectorAll('[data-cart-drawer]').forEach(bindDrawerRemove)
  document.querySelectorAll('[data-cart-page]').forEach(bindCartPage)

  refreshCart().catch(() => {
    /* cart unavailable (e.g. password page); ignore */
  })
}
